import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, DatePicker, Form, Table } from 'antd';
import dayjs from 'dayjs';
import AlertBox from '@/components/alert-box';
import { cn } from '@/libs/utils';
import { DATE_FORMATS } from '@/utils/formUtils';
import {
  CALCULATION_AMOUNT_ROWS,
  CALCULATION_COUNT_ROWS,
  TRAVEL_ABROAD_CURRENCIES,
  TRAVEL_DOMESTIC_CURRENCY,
} from './constants';

const TIMESTAMP_FIELDS = [
  'startDateAndTime',
  'startBorderCrossDateAndTime',
  'endBorderCrossDateAndTime',
  'endDateAndTime',
];

/**
 * Second wizard step — the four trip timestamps and the settlement they yield.
 *
 * Only the timestamps are editable. Every figure below them is computed on the
 * server from the order and the receipts, so the table is a read-back of what
 * was saved rather than a local calculation that could drift from the document.
 */
export default function TravelCalculationStep({
  travelOrder,
  travelOrderCalculation,
  disabled,
  isPending,
  onSubmit,
}) {
  const { t } = useTranslation(['officialTravel']);
  const [form] = Form.useForm();

  const abroadCurrency =
    travelOrder?.dailyWageForAbroadCurrency || TRAVEL_ABROAD_CURRENCIES[0];

  const isAirTravel = (travelOrder?.transportType || []).includes('Airplane');

  // Once the settlement has been saved its own timestamps win; until then the
  // travel order's planned dates are offered as a starting point.
  const hasSavedTimestamps = Boolean(travelOrderCalculation?.startDateAndTime);

  const suggestedDates = useMemo(() => {
    const start = travelOrder?.startDate ? dayjs(travelOrder.startDate) : null;

    if (!start?.isValid()) return { start: null, end: null };

    const days = Number(travelOrder?.numberOfDays) || 1;

    return {
      // Midnight is a placeholder, not a claim about the trip — the warning
      // above the fields says so, and the per diem is counted from these to the
      // hour.
      start: start.startOf('day'),
      end: start.startOf('day').add(Math.max(days - 1, 0), 'day'),
    };
  }, [travelOrder?.startDate, travelOrder?.numberOfDays]);

  const showSuggestionWarning =
    !hasSavedTimestamps && Boolean(suggestedDates.start);

  useEffect(() => {
    form.setFieldsValue(
      TIMESTAMP_FIELDS.reduce((acc, field) => {
        const value = travelOrderCalculation?.[field];

        if (value) {
          acc[field] = dayjs(Number(value));
          return acc;
        }

        // Border crossings are never guessed: they only apply to travel abroad
        // and nothing in the order says when the border was crossed.
        if (!hasSavedTimestamps && field === 'startDateAndTime') {
          acc[field] = suggestedDates.start;
        } else if (!hasSavedTimestamps && field === 'endDateAndTime') {
          acc[field] = suggestedDates.end;
        } else {
          acc[field] = null;
        }

        return acc;
      }, {})
    );
  }, [form, travelOrderCalculation, hasSavedTimestamps, suggestedDates]);

  const formatAmount = (value, isCountry) =>
    `${new Intl.NumberFormat('de-DE', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(Number(value) || 0)} ${
      isCountry ? TRAVEL_DOMESTIC_CURRENCY : abroadCurrency
    }`;

  const dataSource = useMemo(() => {
    const countRows = CALCULATION_COUNT_ROWS.map((key) => ({
      key,
      label: t(`calculation.${key}`, { ns: 'officialTravel' }),
      value: String(travelOrderCalculation?.[key] ?? 0),
      highlighted: false,
    }));

    const reductionPercent =
      Number(travelOrderCalculation?.dailyWageReductionPercent) || 0;

    const amountRows = CALCULATION_AMOUNT_ROWS.filter(
      ({ onlyWithReduction }) => !onlyWithReduction || reductionPercent > 0
    ).map(({ key, country, highlighted }) => ({
      key,
      label: t(`calculation.${key}`, {
        ns: 'officialTravel',
        percent: reductionPercent,
      }),
      value: formatAmount(travelOrderCalculation?.[key], country),
      highlighted: Boolean(highlighted),
    }));

    return [
      ...countRows,
      ...amountRows,
      {
        key: 'attachmentNumber',
        label: t('calculation.attachmentNumber', { ns: 'officialTravel' }),
        value: String(travelOrderCalculation?.attachmentNumber ?? 0),
        highlighted: false,
      },
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [t, travelOrderCalculation, abroadCurrency]);

  const columns = [
    {
      title: t('calculation.columns.label', { ns: 'officialTravel' }),
      dataIndex: 'label',
      key: 'label',
    },
    {
      title: t('calculation.columns.value', { ns: 'officialTravel' }),
      dataIndex: 'value',
      key: 'value',
      align: 'right',
      width: '30%',
    },
  ];

  const handleFinish = (values) => {
    onSubmit(
      TIMESTAMP_FIELDS.reduce((acc, field) => {
        acc[field] = values[field] ? values[field].valueOf() : null;
        return acc;
      }, {})
    );
  };

  return (
    <div className="grid gap-4">
      <Form
        form={form}
        layout="vertical"
        autoComplete="off"
        onFinish={handleFinish}
        className={cn('form grid gap-4')}
      >
        <AlertBox variant="info" className="mb-0">
          <p>{t('calculation.borderCrossingHint', { ns: 'officialTravel' })}</p>

          {/* The regulation measures a flight from the last airport in Serbia,
              not from the border, so the hint only shows when it applies. */}
          {isAirTravel && (
            <p className="mt-2">
              {t('calculation.airTravelHint', { ns: 'officialTravel' })}
            </p>
          )}
        </AlertBox>

        {showSuggestionWarning && (
          <AlertBox variant="warning" className="mb-0">
            <p>{t('calculation.suggestedTimeWarning', { ns: 'officialTravel' })}</p>
          </AlertBox>
        )}

        <div className="rounded-md border bg-zinc-100 p-4">
          <div className="grid gap-x-4 md:grid-cols-2">
            {TIMESTAMP_FIELDS.map((field) => (
              <Form.Item
                key={field}
                name={field}
                label={t(`calculation.${field}`, { ns: 'officialTravel' })}
              >
                <DatePicker
                  showTime={{ format: DATE_FORMATS.TIME_ONLY }}
                  format={DATE_FORMATS.DISPLAY_WITH_TIME}
                  className="w-full"
                  disabled={disabled}
                />
              </Form.Item>
            ))}
          </div>
        </div>

        {!disabled && (
          <div className="form-footer">
            <Button
              shape="round"
              type="primary"
              htmlType="submit"
              className="btn-primary"
              disabled={isPending}
              loading={isPending}
            >
              {t('actions.saveCalculation', { ns: 'officialTravel' })}
            </Button>
          </div>
        )}
      </Form>

      <Table
        dataSource={dataSource}
        columns={columns}
        pagination={false}
        bordered
        size="small"
        rowKey="key"
        rowClassName={(record) => (record.highlighted ? 'font-semibold' : '')}
      />
    </div>
  );
}
